import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  DatePicker,
  Select,
  Button,
  Row,
  Col,
  Typography,
} from "antd";
import {
  CreditCardOutlined,
  UnorderedListOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  UploadOutlined,
} from "@ant-design/icons";
import http from "@/utils/http";
import api from "@/utils/api";
import { convertArrayToDayjs } from "@/utils/date";
import commonFn from "@/utils/commonFn";
import UploadModal from "./component/uploadExcelModal";
/* ---------------- 组件外的静态内容 ---------------- */
const STATUS_STYLE = {
  success: {
    title: "Success",
    color: "#52c41a",
    bg: "#f6ffed",
    border: "#b7eb8f",
    Icon: CheckCircleFilled,
  },
  fail: {
    title: "Failed",
    color: "#ff4d4f",
    bg: "#fff2f0",
    border: "#ffccc7",
    Icon: CloseCircleFilled,
  },
};

// Use Batch 目前只有一个固定选项
const USE_BATCH_OPTIONS = [{ value: "newBatch", label: "New Batch#" }];

// Type 选项
const TYPE_OPTIONS = [
  { value: "debit", label: "Debit / Expense Booking" },
  { value: "upload", label: "Debit / Expense Booking Upload" },
];

const InfoItem = ({ label, valueStyle, children }) => (
  <div style={{ display: "flex", alignItems: "baseline", marginBottom: 16 }}>
    <div style={{ width: 90, flexShrink: 0, color: "#8c8c8c" }}>{label}</div>
    <div
      style={{
        fontWeight: 500,
        color: "#262626",
        minWidth: 0,
        wordBreak: "break-all",
        ...valueStyle,
      }}
    >
      {children}
    </div>
  </div>
);

const StatusBanner = ({ success, msg }) => {
  const s = success ? STATUS_STYLE.success : STATUS_STYLE.fail;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: "10px 12px",
        marginBottom: 20,
        background: s.bg,
        border: `1px solid ${s.border}`,
        borderRadius: 6,
      }}
    >
      <s.Icon style={{ color: s.color, fontSize: 18, marginTop: 2 }} />
      <div>
        <div style={{ color: s.color, fontWeight: 600 }}>{s.title}</div>
        {!success && (
          <div style={{ color: "#595959", fontSize: 13, marginTop: 2 }}>
            {msg || "Submit failed, please try again."}
          </div>
        )}
      </div>
    </div>
  );
};

const RecordDebitPage = ({ record, onSuccess }) => {
  const [form] = Form.useForm();
  const [bookingType, setBookingType] = useState("debit");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [propId, setPropId] = useState(null);
  const [propOptions, setPropOptions] = useState([]);
  const [loadingProps, setLoadingProps] = useState(false);
  const [unitList, setUnitList] = useState([]);
  const [loadingUnit, setLoadingUnit] = useState(false);
  const [vendorList, setVendorList] = useState([]);
  const [loadingVendor, setLoadingVendor] = useState(false);
  const [glAcctList, setGlAcctList] = useState([]);
  const [loadingGlAcct, setLoadingGlAcct] = useState(false);
  const [bankList, setBankList] = useState([]);
  const [loadingBank, setLoadingBank] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);

  const isUpload = bookingType === "upload";

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue({
      type: "debit",
      date1: record?.date ? convertArrayToDayjs(record.date) : undefined,
      amount: record?.amount,
      remark: record?.remark || "",
      checkNo: record?.checkNo || "",
      invoice: "",
      vendor: undefined,
      unit: undefined,
      bankId: undefined,
    });
    setBookingType("debit");
    // setUploadOpen(false);
    setSubmitResult(null);
    setPropId(null);
    setPropOptions([]);
    setUnitList([]);
    setVendorList([]);
    setGlAcctList([]);
    setBankList([]);

    // Prop 支持搜索，初始先拉一次默认列表
    propSearchFn("");
    // Vendor 初始加载
    getVendorList("");
    // Bank 初始加载
    // getBankListFn("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record, form]);

  // Type 切换：清掉右侧上一次的提交结果
  const handleTypeChange = (value) => {
    setBookingType(value);
    setSubmitResult(null);
  };

  // 获取 Prop 列表，支持搜索
  const propSearchFn = async (content = "") => {
    try {
      setLoadingProps(true);
      const response = await http.get(api.propSearch, {
        params: { content },
      });
      const data = response?.data || [];
      const formattedData = data.map((item) => ({
        value: item.propId,
        label: item.propName,
        prop: item.prop,
      }));
      setPropOptions(formattedData);
      return formattedData;
    } catch (err) {
      console.log(err);
      return [];
    } finally {
      setLoadingProps(false);
    }
  };

  // Prop 切换：联动重新拉取 Unit、GL Account、Bank（三者都依赖 propId）
  const handlePropChange = async (value) => {
    setPropId(value);
    form.setFieldsValue({
      unit: undefined,
      glAcct: undefined,
      bankId: undefined,
    });
    setUnitList([]);
    setGlAcctList([]);
    setBankList([]);
    if (!value) {
      return;
    }
    await Promise.all([
      getUnitListFun("", value),
      getGlAcctListFn(value),
      getBankListFn("", value),
    ]);
  };

  // 根据 Prop 获取 Unit 列表，支持搜索
  const getUnitListFun = async (content = "", selectedPropId = propId) => {
    if (!selectedPropId) {
      setUnitList([]);
      return [];
    }
    try {
      setLoadingUnit(true);
      const response = await http.get(api.unitSearch, {
        params: {
          propId: selectedPropId,
          content,
        },
      });
      if (response?.code === "1") {
        const list = (response.data || []).map((item) => ({
          value: item.unitId,
          label: item.unitName,
        }));
        setUnitList(list);
        return list;
      }
      setUnitList([]);
      return [];
    } catch (err) {
      console.log(err);
      setUnitList([]);
      return [];
    } finally {
      setLoadingUnit(false);
    }
  };

  const getGlAcctListFn = async (selectedPropId) => {
    if (!selectedPropId) {
      setGlAcctList([]);
      return;
    }
    try {
      setLoadingGlAcct(true);
      const response = await http.get(api.getShowGlAcctByProp, {
        params: {
          propId: selectedPropId,
          acctType: ["C", "E"].toString(),
          active: 1,
        },
      });
      if (response?.code === "1") {
        setGlAcctList(
          (response.data || []).map((item) => ({
            value: item.glAcct,
            label: item.glAcctTitle,
            title: item.title,
          })),
        );
      } else {
        setGlAcctList([]);
        commonFn.modalFailedFn(response);
      }
    } catch (err) {
      console.log(err);
      setGlAcctList([]);
    } finally {
      setLoadingGlAcct(false);
    }
  };

  const getVendorList = async (content = "") => {
    try {
      setLoadingVendor(true);
      const response = await http.get(api.vendorSearch, {
        params: { content },
      });
      if (response?.code === "1") {
        const list = (response.data || []).map((item) => ({
          value: item.vendid,
          label: item.vendorName,
          vendorId: item.vendorId,
        }));
        setVendorList(list);
        return list;
      }
      return [];
    } catch (err) {
      console.log(err);
      return [];
    } finally {
      setLoadingVendor(false);
    }
  };

  // 获取 Bank 列表，支持搜索，依赖当前选中的 propId
  const getBankListFn = async (content = "", selectedPropId = propId) => {
    try {
      setLoadingBank(true);
      const response = await http.get(api.bank, {
        params: { content, propId: selectedPropId },
      });
      if (response.code === "1") {
        setBankList(response?.data || []);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingBank(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const values = await form.validateFields();
      const params = {
        date1: values.date1?.format("MM/DD/YYYY"),
        amount: values.amount,
        propId: values.prop,
        glAcct: values.glAcct,
        remark: values.remark,
        vendor: values.vendor,
        checkNo: values.checkNo,
        bankId: values.bankId,
        invoice: values.invoice,
      };
      const res = await http.post(api.getDebitExpenseSave, params);
      const isSuccess = res?.code === "1" || res?.code === 1;
      setSubmitResult({
        success: isSuccess,
        msg: res?.msg,
        ...(isSuccess ? res?.data : {}),
      });

      if (isSuccess) {
        onSuccess?.(res);
      }
    } catch (err) {
      if (err?.errorFields) {
        return;
      }
      setSubmitResult({
        success: false,
        msg: err?.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const resultData = submitResult;

  return (
    <div className="record-debit-page" style={{ minHeight: "100%" }}>
      {/* 顶部标题栏 */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "16px 24px",
          borderBottom: "1px solid #d9d9d9",
        }}
      >
        <CreditCardOutlined style={{ fontSize: 18 }} />
        <Typography.Title level={5} style={{ margin: 0, letterSpacing: 1 }}>
          DEBIT / EXPENSE BOOKING
        </Typography.Title>
      </div>

      <Row gutter={0}>
        {/* 左侧：表单区域 */}
        <Col span={5} style={{ paddingRight: 16 }}>
          <div
            style={{
              background: "#fff",
              border: "1px solid #f0f0f0",
              borderRadius: 6,
              padding: 20,
            }}
          >
            <Form
              form={form}
              layout="horizontal"
              labelCol={{ span: 5 }}
              wrapperCol={{ span: 19 }}
              size="middle"
              style={{ width: "100%" }}
            >
              <Form.Item name="type" label="Type" initialValue="debit">
                <Select options={TYPE_OPTIONS} onChange={handleTypeChange} />
              </Form.Item>

              {/* Post Date 以下：仅 Debit 模式显示 */}
              {!isUpload && (
                <>
                    <Form.Item
                name="date1"
                label="Post Date"
                rules={[{ required: true, message: "Post Date is required!" }]}
              >
                <DatePicker style={{ width: "100%" }} format="MM/DD/YYYY" />
              </Form.Item>
                  {commonFn.formInputNumberDoc({
                    name: "amount",
                    label: "Amount",
                    form,
                    allowNegative: true,
                  })}

                  <Form.Item
                    name="prop"
                    label="Prop"
                    rules={[
                      { required: true, message: "Property is required!" },
                    ]}
                  >
                    <Select
                      placeholder="Nothing Selected"
                      options={propOptions}
                      loading={loadingProps}
                      allowClear
                      showSearch
                      filterOption={false}
                      onSearch={propSearchFn}
                      onChange={handlePropChange}
                    />
                  </Form.Item>

                  <Form.Item name="unit" label="Unit">
                    <Select
                      placeholder="Nothing selected"
                      showSearch
                      allowClear
                      loading={loadingUnit}
                      filterOption={false}
                      disabled={!propId}
                      onSearch={(content) => getUnitListFun(content, propId)}
                      options={unitList}
                    />
                  </Form.Item>

                  <Form.Item
                    name="glAcct"
                    label="GL Account"
                    rules={[
                      { required: true, message: "GL Account is required!" },
                    ]}
                  >
                    <Select
                      placeholder="Nothing selected"
                      showSearch
                      allowClear
                      loading={loadingGlAcct}
                      disabled={!propId}
                      optionFilterProp="label"
                      options={glAcctList}
                    />
                  </Form.Item>

                  <Form.Item
                    name="remark"
                    label="Remark"
                    rules={[{ required: true, message: "Remark is required!" }]}
                  >
                    <Input placeholder="Enter remark" allowClear />
                  </Form.Item>

                  <Form.Item
                    name="vendor"
                    label="Vendor"
                    rules={[{ required: true, message: "Vendor is required!" }]}
                  >
                    <Select
                      placeholder="Enter vendor"
                      showSearch
                      allowClear
                      loading={loadingVendor}
                      filterOption={false}
                      onSearch={getVendorList}
                      options={vendorList}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Check No."
                    name="checkNo"
                    rules={[
                      { required: true, message: "Check No. is required!" },
                    ]}
                  >
                    <Input placeholder="000000" />
                  </Form.Item>

                  <Form.Item
                    name="bankId"
                    label="Bank"
                    rules={[{ required: true, message: "Bank is required!" }]}
                  >
                    <Select
                      showSearch
                      allowClear
                      loading={loadingBank}
                      onSearch={getBankListFn}
                      optionFilterProp="label"
                      filterOption={(input, option) =>
                        (option?.name ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      fieldNames={{
                        value: "bankId",
                        label: "name",
                      }}
                      options={bankList}
                    />
                  </Form.Item>

                  <Form.Item label="Use Batch" name="useBatch">
                    <Select
                      placeholder="New Batch#"
                      allowClear
                      options={USE_BATCH_OPTIONS}
                    />
                  </Form.Item>

                  <Form.Item
                    name="invoice"
                    label="Invoice"
                    rules={[
                      { required: true, message: "Invoice is required!" },
                    ]}
                  >
                    <Input placeholder="Enter invoice" allowClear />
                  </Form.Item>
                </>
              )}
            </Form>

            <div style={{ textAlign: "center", marginTop: 8 }}>
              {isUpload ? (
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  onClick={() => setUploadOpen(true)}
                  style={{ minWidth: 160, height: 40 }}
                >
                  Upload
                </Button>
              ) : (
                <Button
                  type="primary"
                  loading={submitting}
                  disabled={!!submitResult?.success}
                  onClick={handleSubmit}
                  style={{ minWidth: 160, height: 40 }}
                >
                  Submit
                </Button>
              )}
            </div>
          </div>
        </Col>

        {/* 右侧：Information Detail */}
        <Col span={19} style={{ paddingLeft: 16 }}>
          <div
            style={{
              background: "#fff",
              border: "1px solid #f0f0f0",
              borderRadius: 6,
              minHeight: 500,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "16px 20px",
                borderBottom: "1px solid #f0f0f0",
                fontWeight: 500,
                fontSize: 16,
              }}
            >
              <UnorderedListOutlined />
              Information Detail
            </div>

            <div style={{ padding: 40 }}>
              {resultData && !isUpload ? (
                <>
                  <StatusBanner
                    success={resultData.success}
                    msg={resultData.msg}
                  />
                  {resultData.success && (
                    <>
                      <InfoItem
                        label="Amount"
                        valueStyle={{
                          fontSize: 16,
                          fontWeight: 600,
                          color: "#1677ff",
                        }}
                      >
                        {commonFn.amountToDollarBracketFn(resultData.amount)}
                      </InfoItem>
                      <InfoItem label="Batch">
                        {resultData.batch ?? "-"}
                      </InfoItem>
                    </>
                  )}
                </>
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    color: "#8c8c8c",
                    fontSize: 22,
                    lineHeight: 2,
                    paddingTop: 40,
                  }}
                >
                  <div>
                    {isUpload
                      ? "To upload Debit / Expense Bookings in bulk, Please Click the Upload Button on the Left."
                      : "To Enter a Debit / Expense Booking, Please Fill out the Information on the Left and Click Submit."}
                  </div>
                  <div style={{ marginTop: 16 }}>
                    Debit / Expense Booking function allows users to enter
                    money collected and record transactions both negative and
                    positive.
                  </div>
                </div>
              )}
            </div>
          </div>
        </Col>
      </Row>

      <UploadModal
        open={uploadOpen}
        onCancel={() => setUploadOpen(false)}
        onSuccess={() => {
          // setUploadOpen(false);
          onSuccess?.();
        }}
      />
    </div>
  );
};

export default RecordDebitPage;
